// Copyright (c) Ping. All rights reserved.

import React from 'react';
import {
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { createTheme } from '../../design-system/theme';
import { LEADERBOARD_COLUMN_WIDTH } from './LeaderboardColumnHeader';
import { leaderboardStrings } from './strings';
import type { LeaderboardColumn, LeaderboardEntry } from './types';

export interface LeaderboardEntryRowProps {
  readonly entry: LeaderboardEntry;
  readonly columns: readonly LeaderboardColumn[];
}

function renderCell(value: string | number | null): string {
  if (value === null) {
    return leaderboardStrings.emptyCellPlaceholder;
  }
  if (typeof value === 'number') {
    return String(value);
  }
  return value;
}

export const LeaderboardEntryRow = React.memo<LeaderboardEntryRowProps>(({ entry, columns }) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');

  return (
    <View
      style={[
        styles.row,
        {
          backgroundColor: theme.colors.surface.primary,
          borderBottomColor: theme.colors.border.secondary,
        },
      ]}
      accessibilityLabel={`Entry ${entry.id}`}
    >
      {columns.map((column) => (
        <View
          key={column.id}
          style={[styles.cell, { borderRightColor: theme.colors.border.secondary }]}
        >
          <Text style={[styles.cellText, { color: theme.colors.text.primary }]} numberOfLines={1}>
            {renderCell(entry.values.get(column.id) ?? null)}
          </Text>
        </View>
      ))}
    </View>
  );
});

LeaderboardEntryRow.displayName = 'LeaderboardEntryRow';

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    borderBottomWidth: StyleSheet.hairlineWidth,
  } satisfies ViewStyle,
  cell: {
    width: LEADERBOARD_COLUMN_WIDTH,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRightWidth: StyleSheet.hairlineWidth,
  } satisfies ViewStyle,
  cellText: {
    fontSize: 13,
    fontWeight: '500',
  } satisfies TextStyle,
});
